import { Box, Button, Stack, TextField } from "@mui/material";
import { useState } from "react";
import { ApiError, requestPasswordReset, signIn } from "../../data/api";
import { bareMessage } from "../../data/data";
import { Failure, Field } from "../../ui/components/primitives";
import { useTokens } from "../../ui/theme/theme";
import { AuthLayout } from "./AuthLayout";

// ---
// Sign-in only
//
// No self-service registration: accounts are provisioned, and a public create-account form on an
// IAM console is an open door to the identity store. The first account is the seeded platform
// administrator.
// ---

const PROVISIONED =
  "Accounts are provisioned by an administrator. There is no self-service sign-up.";

export function SignIn({ onDone }: { onDone: () => void }): React.JSX.Element {
  const tokens = useTokens();
  const [identifier, setIdentifier] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [forgot, setForgot] = useState(false);
  const [email, setEmail] = useState("");
  const [requested, setRequested] = useState(false);

  async function requestReset(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestPasswordReset(email);
      // The same screen for a known and an unknown address: the server does not say which, and
      // neither does this.
      setRequested(true);
    } catch (caught) {
      setError(bareMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  async function submit(event: React.FormEvent): Promise<void> {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await signIn(identifier, password);
      onDone();
    } catch (caught) {
      // The server's message for bad credentials is deliberately generic. Echoing it rather than
      // inventing our own keeps the console from becoming an account-enumeration oracle.
      setError(bareMessage(caught));
    } finally {
      setBusy(false);
    }
  }

  function backToSignIn(): void {
    setError(null);
    setForgot(false);
    setRequested(false);
  }

  if (forgot && requested) {
    return (
      <AuthLayout
        title="Check your email"
        subtitle={
          <>
            If an account exists for{" "}
            <Box component="strong" sx={{ color: tokens.ink.primary }}>
              {email}
            </Box>
            , a reset link is on its way. It works once and expires in an hour.
          </>
        }
        footer={PROVISIONED}
      >
        <Button variant="outlined" size="large" fullWidth onClick={backToSignIn}>
          Back to sign in
        </Button>
      </AuthLayout>
    );
  }

  if (forgot) {
    return (
      <AuthLayout
        title="Reset your password"
        subtitle="Enter the account's email address and we will send a link to choose a new password."
        footer={PROVISIONED}
      >
        <form onSubmit={requestReset}>
          <Stack spacing={4}>
            <Field label="Email">
              <TextField
                type="email"
                value={email}
                onChange={(event) => setEmail(event.target.value)}
                required
                autoComplete="email"
                fullWidth
                autoFocus
              />
            </Field>
            <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
              {busy ? "Sending…" : "Send reset link"}
            </Button>
            <Button variant="text" onClick={backToSignIn} fullWidth>
              Back to sign in
            </Button>
          </Stack>
          <Failure error={error} />
        </form>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title="Welcome to FlexAuth"
      subtitle="Sign in to the administration console"
      footer={PROVISIONED}
    >
      <form onSubmit={submit}>
        <Stack spacing={4}>
          <Field label="Username or email">
            <TextField
              value={identifier}
              onChange={(event) => setIdentifier(event.target.value)}
              required
              autoComplete="username"
              fullWidth
              autoFocus
            />
          </Field>
          <Field label="Password">
            <TextField
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              required
              autoComplete="current-password"
              fullWidth
            />
          </Field>
          <Button type="submit" variant="contained" size="large" disabled={busy} fullWidth>
            {busy ? "Signing in…" : "Sign in"}
          </Button>
          <Button
            variant="text"
            onClick={() => {
              setError(null);
              setForgot(true);
            }}
            sx={{ alignSelf: "center" }}
          >
            Forgot password?
          </Button>
        </Stack>
      </form>
      <Failure error={error} />
    </AuthLayout>
  );
}
